import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { publishUser, useGestionnairePermissions } from './useGestionnairePermissions';
import { contractDataForViewer } from '@/lib/contractPrivacy';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Un seul appel /auth/me partagé entre tous les composants montés en même temps (perf).
let meInflight = null;
const fetchMeDeduped = (token) => {
  if (!meInflight) {
    meInflight = axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    meInflight.finally(() => setTimeout(() => { meInflight = null; }, 1500)).catch(() => {});
  }
  return meInflight;
};

export const useAuth = () => {
  const [user, setUser] = useState(() => {
    // Initialize from localStorage immediately to avoid loading flash
    try {
      const stored = localStorage.getItem('user');
      const saved = stored ? JSON.parse(stored) : null;
      const visible = contractDataForViewer(saved, saved?.role);
      if (stored && JSON.stringify(visible) !== stored) localStorage.setItem('user', JSON.stringify(visible));
      return visible;
    } catch { return null; }
  });
  const [loading, setLoading] = useState(() => {
    // If we have a cached user, skip loading state
    try { return !localStorage.getItem('user'); } catch { return true; }
  });
  const heartbeatRef = useRef(null);
  const refreshUserRef = useRef(() => {});
  useGestionnairePermissions(user, setUser);

  const startHeartbeat = useCallback((token) => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    const sendHeartbeat = () => {
      axios.post(`${API}/auth/heartbeat`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    };
    sendHeartbeat();
    heartbeatRef.current = setInterval(sendHeartbeat, 60000);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchMeDeduped(token)
      .then(res => {
        // Iter 416 : session persistante - le backend renvoie un jeton neuf (365j)
        // dans X-Renewed-Token tant que l'utilisateur ne se deconnecte pas.
        const renewed = res.headers['x-renewed-token'];
        const activeToken = renewed || token;
        if (renewed) localStorage.setItem('token', renewed);
        const visible = contractDataForViewer(res.data, res.data?.role);
        setUser(visible);
        publishUser(visible);
        startHeartbeat(activeToken);
        setLoading(false);
      })
      .catch((err) => {
        // Iter 420 : ne deconnecter QUE si le serveur rejette vraiment le jeton
        // (401/403). Une erreur reseau/timeout (PWA rouverte hors ligne, backend
        // en cours de reveil) ne doit PAS effacer la session.
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        } else {
          // Erreur transitoire : on garde la session en cache et on reessaie.
          startHeartbeat(token);
          setTimeout(() => refreshUserRef.current(), 10000);
        }
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
    return () => stopHeartbeat();
  }, [startHeartbeat, stopHeartbeat]);

  // Send logout on page close
  useEffect(() => {
    const handleUnload = () => {
      const token = localStorage.getItem('token');
      if (token) {
        navigator.sendBeacon(`${API}/auth/heartbeat-stop`, JSON.stringify({ token }));
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  const login = (token, userData) => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('store_plan_cache');
    // NOTE: do NOT remove enseigne_cache here - it's set before login
    localStorage.setItem('token', token);
    const visible = contractDataForViewer(userData, userData?.role);
    localStorage.setItem('user', JSON.stringify(visible));
    setUser(visible);
    startHeartbeat(token);
    // Reset enseigne fetch flag for new session
    window.__enseigneFetched = false;
  };

  const logout = async () => {
    const token = localStorage.getItem('token');
    stopHeartbeat();
    if (token) {
      try {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch {}
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('enseigne_cache');
    localStorage.removeItem('enseigne_logo_data');
    localStorage.removeItem('store_plan_cache');
    window.__enseigneFetched = false;
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      const renewed = res.headers['x-renewed-token'];
      if (renewed) localStorage.setItem('token', renewed);
      const visible = contractDataForViewer(res.data, res.data?.role);
      setUser(visible);
      publishUser(visible);
    } catch {}
  };
  refreshUserRef.current = refreshUser;

  return { user, loading, login, logout, refreshUser };
};
